/**
 * "Delete the drops outside the window" (user, 19/10/2026: "I want a button
 * to delete all slots who is outside the window") — only drops nothing would
 * miss: outside the window (or past the end) on today's run date AND on every
 * clock-change day of the next year. Start, end and pins are Danish times, so
 * a drop outside today may come inside after a change: kept (`seasonal`).
 * Deleting must never move a drop that goes: a pinned drop takes no tick, so
 * it can go; a drop on the clock only from the trailing block (after the last
 * clock drop that goes) — deleting an earlier one pulls the next ones into its
 * tick. Leading drops (before the window opens) are only counted: the honest
 * fix is the start. Never a drop sent anyway (🌙), never a route test's
 * drops (before the window on purpose, every one — 20/10/2026).
 * For Media Devoted | Created By Ciro Autiero
 */
import { slotKey, type SlotTime } from '../slot-times';

export interface RemovalDay {
  /** The country's run date "YYYY-MM-DD" — today first, then the clock-change days in date order. */
  runDate: string;
  /** The timing mirror's drop times that day, by slot key. */
  times: Map<string, SlotTime>;
}

export interface RemovalSlot {
  id: string | null;
  enabled: boolean;
  pinnedAtMin: number | null;
}

export interface RemovalOptions {
  routeTest: boolean;
  /** The country's window opens (a country minute) — a clock drop outside before it is "leading". */
  windowStartMin: number;
}

export interface OutsideRemoval {
  /** Draft positions to delete, ascending. */
  remove: number[];
  /** Of those, the ones only past the end time (the trailing block's tail). */
  pastEnd: number;
  /** Clock drops outside today before the window opens — moving the start fixes them, deleting does not. */
  leading: number;
  /** Outside today, inside from a later clock change (DD/MM/YYYY, the earliest) — kept. */
  seasonal: { count: number; insideFrom: string } | null;
  /** Outside today but sent anyway (the drop's own 🌙) — kept. */
  sentAnyway: number;
  /** The route test's drops are outside today — before the window on purpose, kept. */
  routeTest: boolean;
  /** Outside on every day, but a later clock drop goes — deleting would move it. Kept. */
  held: number;
}

interface Ctx {
  slots: RemovalSlot[];
  days: RemovalDay[];
  /** A route test: its drops are never outside — they go before the window on purpose. */
  routeTest: boolean;
  windowStartMin: number;
}

const gone = (t: SlotTime | undefined): boolean => t !== undefined && (t.clamped || t.outsideWindow === true);
const dmy = (iso: string): string => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
const timeOn = (ctx: Ctx, day: RemovalDay, i: number) => day.times.get(slotKey(ctx.slots[i], i));
const today = (ctx: Ctx, i: number) => timeOn(ctx, ctx.days[0], i);
const outsideToday = (ctx: Ctx, i: number): boolean => !ctx.routeTest && today(ctx, i)?.outsideWindow === true;
const goneAllYear = (ctx: Ctx, i: number): boolean => !ctx.routeTest && ctx.days.every((d) => gone(timeOn(ctx, d, i)));
const beforeOpen = (ctx: Ctx, i: number): boolean => outsideToday(ctx, i) && (today(ctx, i)?.minute ?? 0) < ctx.windowStartMin;

export function outsideRemoval(slots: RemovalSlot[], days: RemovalDay[], opts: RemovalOptions): OutsideRemoval {
  const ctx: Ctx = { slots, days, routeTest: opts.routeTest, windowStartMin: opts.windowStartMin };
  const enabled = slots.flatMap((s, i) => (s.enabled ? [i] : []));
  const clock = enabled.filter((i) => slots[i].pinnedAtMin === null);
  const leading = new Set(clock.filter((i) => beforeOpen(ctx, i)));
  const remove = [...pinnedGone(ctx, enabled), ...trailingBlock(ctx, clock, leading)].sort((a, b) => a - b);
  const removed = new Set(remove);
  const kept = enabled.filter((i) => outsideToday(ctx, i) && !removed.has(i) && !leading.has(i));
  return {
    remove,
    pastEnd: remove.filter((i) => !outsideToday(ctx, i)).length,
    leading: leading.size,
    seasonal: seasonalOf(ctx, kept.filter((i) => !goneAllYear(ctx, i))),
    sentAnyway: enabled.filter((i) => !ctx.routeTest && today(ctx, i)?.sentOutside === true).length,
    routeTest: ctx.routeTest && enabled.some((i) => today(ctx, i)?.sentOutside === true || today(ctx, i)?.outsideWindow === true),
    held: kept.filter((i) => goneAllYear(ctx, i)).length,
  };
}

/** Pinned drops outside today and gone on every day — no other drop takes their tick. */
function pinnedGone(ctx: Ctx, enabled: number[]): number[] {
  return enabled.filter((i) => ctx.slots[i].pinnedAtMin !== null && outsideToday(ctx, i) && goneAllYear(ctx, i));
}

/** The clock drops after the last one that goes on any day (never a leading one) — when one of them is outside today. */
function trailingBlock(ctx: Ctx, clock: number[], leading: Set<number>): number[] {
  const block: number[] = [];
  for (let k = clock.length - 1; k >= 0 && !leading.has(clock[k]) && goneAllYear(ctx, clock[k]); k -= 1) block.push(clock[k]);
  return block.some((i) => outsideToday(ctx, i)) ? block : [];
}

/** The drops outside only until a clock change — and the first day one of them goes. */
function seasonalOf(ctx: Ctx, seasonal: number[]): OutsideRemoval['seasonal'] {
  if (seasonal.length === 0) return null;
  const from = ctx.days.find((d) => seasonal.some((i) => !gone(timeOn(ctx, d, i))));
  return { count: seasonal.length, insideFrom: dmy(from?.runDate ?? ctx.days[0].runDate) };
}
